import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, MapPin, Phone, Star } from "lucide-react";
import {
    SERVICES,
    SERVICE_IMAGES,
    SERVICE_AREAS,
    COMPANY,
    TESTIMONIALS,
} from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import { serviceJsonLd, breadcrumbJsonLd, SITE_BASE_URL } from "../lib/jsonld";
import { ContactForm } from "../components/sections/ContactForm";

export default function ServiceDetail() {
    useReveal();
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.id === slug);

    useSeo(
        service
            ? {
                  title: `${service.title} | HAZ Insulation · Chicagoland & NW Indiana`,
                  description: `${service.title} services from HAZ Insulation. ${service.blurb} BPI-certified crew, NICOR & ComEd rebate partner — up to ${COMPANY.maxRebate} back. Serving Chicago Southland and Northwest Indiana.`,
                  canonical: `${SITE_BASE_URL}/services/${service.id}`,
                  jsonLd: [
                      serviceJsonLd({
                          name: service.title,
                          description: service.details || service.blurb,
                          url: `${SITE_BASE_URL}/services/${service.id}`,
                      }),
                      breadcrumbJsonLd([
                          { name: "Home", url: "/" },
                          { name: "Services", url: "/services" },
                          { name: service.title, url: `/services/${service.id}` },
                      ]),
                  ],
              }
            : undefined
    );

    if (!service) return <Navigate to="/services" replace />;

    const photo = SERVICE_IMAGES[service.id];
    const others = SERVICES.filter((s) => s.id !== service.id).slice(0, 6);

    return (
        <main data-testid={`service-detail-${service.id}`} className="pt-24">
            {/* Hero */}
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                {photo && (
                    <img
                        src={photo}
                        alt={service.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <Link
                        to="/#services"
                        data-testid="service-back-link"
                        className="inline-flex items-center gap-2 text-sm text-bone/70 hover:text-ember mb-8 reveal"
                    >
                        <ArrowLeft size={16} /> All services
                    </Link>
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Service
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        {service.title}
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        {service.blurb}
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <a
                            href="#quote"
                            data-testid="service-cta-quote"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Get a Free Quote <ArrowRight size={18} />
                        </a>
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="service-cta-phone"
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Detail */}
            {service.details && (
                <section className="py-20 md:py-28 bg-bone">
                    <div className="max-w-4xl mx-auto px-6 md:px-12">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                            <span className="h-px w-10 bg-moss inline-block align-middle mr-3" />
                            Our Process
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-8 reveal">
                            How we do {service.title.toLowerCase()}.
                        </h2>
                        <p className="text-lg text-ink/85 leading-relaxed reveal">
                            {service.details}
                        </p>
                        {service.bullets && (
                            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 reveal">
                                {service.bullets.map((b) => (
                                    <div
                                        key={b}
                                        className="flex items-start gap-3 border border-line p-4"
                                    >
                                        <CheckCircle2
                                            size={20}
                                            strokeWidth={1.5}
                                            className="text-copper shrink-0 mt-0.5"
                                        />
                                        <span className="text-sm font-semibold uppercase tracking-wider">
                                            {b}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Why HAZ for this service */}
            <section className="py-20 md:py-28 bg-moss text-bone relative overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7 reveal">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5">
                            Why HAZ for {service.title}
                        </div>
                        <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight">
                            BPI certified, NICOR-approved, real crew.
                        </h2>
                    </div>
                    <ul className="lg:col-span-5 space-y-4 reveal">
                        {[
                            "BPI-certified owner-operator on every quote",
                            `Up to ${COMPANY.maxRebate} in NICOR & ComEd rebates filed for you`,
                            "Thermal imaging + blower door verification",
                            "Same crew, same standard — Jamie, Jose, Denni, Juan",
                            "50+ five-star Google reviews",
                        ].map((p) => (
                            <li key={p} className="flex items-start gap-3 text-bone/90">
                                <CheckCircle2
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-ember shrink-0 mt-1"
                                />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Service areas */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Available in
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-[1.05] mb-10 reveal">
                        {service.title} across Chicago Southland & NW Indiana.
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
                        {SERVICE_AREAS.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/areas/${a.slug}`}
                                data-testid={`service-area-link-${a.slug}`}
                                className="bg-bone p-5 hover:bg-white transition-colors flex items-center gap-3"
                            >
                                <MapPin
                                    size={16}
                                    strokeWidth={1.5}
                                    className="text-copper shrink-0"
                                />
                                <div>
                                    <div className="font-heading font-bold text-sm">
                                        {a.city}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {a.state} · {a.zip}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Other services */}
            <section className="py-20 md:py-28 bg-bone border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Related services
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        We also do:
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {others.map((o) => (
                            <Link
                                key={o.id}
                                to={`/services/${o.id}`}
                                data-testid={`related-service-${o.id}`}
                                className="bg-white border border-line p-7 hover:-translate-y-1 transition-transform group flex flex-col justify-between min-h-[180px]"
                            >
                                <h3 className="font-heading text-xl font-bold tracking-tight mb-2 group-hover:text-copper transition-colors">
                                    {o.title}
                                </h3>
                                <p className="text-sm text-muted_ink leading-relaxed">
                                    {o.blurb}
                                </p>
                                <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-copper">
                                    Learn more <ArrowRight size={12} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust strip */}
            <section className="py-12 bg-ink text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-sm">
                    <div className="flex items-center gap-2">
                        <div className="flex">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star
                                    key={i}
                                    size={14}
                                    className="fill-ember text-ember"
                                />
                            ))}
                        </div>
                        <span className="font-semibold">{TESTIMONIALS.length}+ five-star reviews</span>
                    </div>
                    <span>·</span>
                    <span className="font-semibold">NICOR & ComEd Rebate Partner</span>
                    <span>·</span>
                    <span className="font-semibold">BPI Certified</span>
                    <span>·</span>
                    <span className="font-semibold">Licensed IL & IN</span>
                </div>
            </section>

            {/* Contact */}
            <ContactForm />
        </main>
    );
}
